import { useMemo, useState } from 'react';
import { useTranslation, Drawer, Search, MLPVersion, useSession } from '@nextgen-web-framework/all';
import { IProduct, ProductData, productDataTransformer } from '@commerce-product-webcomponents/sdk';
import { SearchType, getPrice, searchApi } from '@commerce-search-webcomponents/sdk';
import { SearchResultCardComponent } from '@commerce-product-webcomponents/ui';
import { StorageService } from '@nextgen-web-framework/core';
import { CartConstantsEnum } from '@commerce-cart-webcomponents/sdk';
import { debounce } from 'lodash';
import { getProductDataById } from '../../../../utils/commonUtils';
import { AddToCartButtonType } from '../../../add-to-cart-button/types/enums';
import { DynamicBundleDrawer } from '../../../../common/components/dynamic-bundle-drawer/DynamicBundleDrawer';
import { useExceptionToast } from '../../../../common';
import styles from './AddProductsDrawer.module.scss';
import { AddProductsDrawerLoader } from './add-products-drawer-loader/AddProductsDrawerLoader';

export type AddProductsDrawerProps = {
  onDrawerClose?: () => void;
};
export const AddProductsDrawer = ({ onDrawerClose }: AddProductsDrawerProps) => {
  const t = useTranslation();
  const [searchFieldVal, setSearchFieldVal] = useState('');
  const [productList, setProductList] = useState<IProduct[]>([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showBundleDrawer, setShowBundleDrawer] = useState(false);
  const [bundleProduct, setBundleProduct] = useState<IProduct>();
  const sessionDetails = useSession();
  const triggerToast = useExceptionToast();

  async function searchItem(searchText?: string) {
    setProductList([]);
    setShowLoader(true);
    try {
      const data = await searchApi.getSearchResults({
        version: 'v1',
        payload: {
          keywords: searchText ?? searchFieldVal,
          searchType: SearchType.Fulltext,
          limit: '20',
          offset: '0',
          warehouseId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.WarehouseId),
        },
      });
      const products = data.products?.map((p) => ({
        ...productDataTransformer(p as ProductData),
        productPrice: getPrice(p, sessionDetails) as number,
      }));

      if (products) {
        setProductList(products);
      }
    } catch (error) {
      triggerToast();
    } finally {
      setShowLoader(false);
    }
  }
  const onSearchValuechangeHandler = (value: string) => {
    setSearchFieldVal(value);
    if (value === '') {
      setProductList([]);
    }
  };

  const handleViewBundleClick = async (product: IProduct) => {
    const productInfo = await getProductDataById(product.productId);
    if (!(productInfo instanceof Response)) {
      setBundleProduct(productDataTransformer(productInfo));
      setShowBundleDrawer(true);
    }
  };

  const handleProductSearch = useMemo(() => debounce(searchItem, 300), []);
  const showDrawer = () => {
    if (showBundleDrawer) {
      return <DynamicBundleDrawer onClose={() => setShowBundleDrawer(false)} productData={bundleProduct} />;
    }

    return (
      <Drawer
        closeIcon={true}
        bottomGap={0}
        onClose={onDrawerClose}
        header={t('cart.listBanner.addProducts')}
        closeOnBackdropClick={true}
        subHeader={
          <div className={styles['searchBox-container']}>
            <Search
              value={searchFieldVal}
              placeholder={t('cart.addProductsDrawer.searchPlaceholder')}
              onSearch={() => {
                searchItem();
              }}
              onChange={(e) => {
                onSearchValuechangeHandler(e.target.value);
                // Calling search Item on text Change.
                //delay of 300 ms
                handleProductSearch(e.target.value);
                //searchItem(e.target.value);
              }}
              onClear={() => {
                setProductList([]);
                setSearchFieldVal('');
              }}
              mlpVersion={MLPVersion.TWO}
            />
          </div>
        }
      >
        <div className={styles['set-minimum-height']}>
          {showLoader ? (
            <AddProductsDrawerLoader />
          ) : (
            productList.map((product) => (
              <div key={product.productId}>
                <SearchResultCardComponent
                  showLink={false}
                  onClick={() => handleViewBundleClick(product)}
                  disabled={false}
                  searchedKeyword={searchFieldVal}
                  addToCartButtonType={AddToCartButtonType.ADD_TO_LIST_BUTTON}
                  productData={product}
                  showSubText
                  entryObj={{
                    ...product,
                    brandName: product.brandName as string,
                  }}
                />
              </div>
            ))
          )}
        </div>
      </Drawer>
    );
  };

  return showDrawer();
};
